import { useState, useEffect, useCallback, useRef, memo, useMemo, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { useSession } from '../../contexts/SessionContext';
import { useConnectionContext, useConnectionData } from '../../contexts/ConnectionStateContext';
import AnonConnectionPage from '../../components/Connection/AnonConnectionPage';
import AnonLandingPage from '../../components/Connection/variants/chat/AnonLandingPage';
import { joinChatRoom } from '../../services/chat/chatService';
import {
  logFunnelQRScanned,
  logFunnelExchangeCompleted,
  logConnectionError,
} from '../../services/analytics/eventLogger';
import { hasAnonParticipation } from '../../utils/landingGate';
import {
  isSansChatVariant,
  isTextVariant,
  isThreadVariant,
  hasDownstreamLadder,
} from '../../utils/exchangeVariant';
import { isNewExperience } from '../../utils/newExperience';
import { safariBleed } from '../../hooks/useShellChrome';
import { prefetchedLook } from '../../services/exchange/prefetchedLook';
import { isSimpleTier } from '../../utils/userTier';
import AnonCardLanding from '../../components/Connection/variants/card/AnonCardLanding';
import AnonShareBackFlow from '../../components/Connection/variants/card/AnonShareBackFlow';
import AnonTextFlow from '../../components/Connection/variants/text/AnonTextFlow';
import AnonThreadInterstitial from '../../components/Connection/variants/thread/AnonThreadInterstitial';
import { useNavigation } from '../../contexts/NavigationContext';
import { commitAnonContactRecord, announceAnonName } from '../../services/sharing/anonShareBack';
import { saveContactToPhone } from '../../utils/contactUtils';
import { getAnonUserId } from '../../utils/identityUtils';
import { ensureAnonIdentity } from '../../services/auth/anonIdentity';
import { currentUtcOffsetMinutes } from '../../utils/dateHelpers';
import { confirmGuestConnection, checkHostConfirmed } from '../../services/activeSession';
import { signalHostOfGuest } from '../../services/guestReturn';
import {
  getAnonData,
  getConnectionMetadata,
  upsertConnectionView,
} from '../../services/storage/userDataStorage';
import {
  buildReceivedChatMessage,
  buildContactShareMessage,
} from '../../services/chat/messageBuilders';
import { startTiming, mark, flush } from '../../services/connectionTiming';
import { hasData } from '../../utils/objectHelpers';

// The new design's guest view (signup E3a) — its own chunk: main never loads it.
const GuestExchangeFlow = lazy(() => import('../NewExperience/GuestExchange/GuestExchangeFlow'));

/**
 * AnonExchangeFlow Component
 *
 * OPTIMIZED for speed - shows AnonConnectionPage as fast as possible:
 * 1. Extracts chatRoomId from URL and updates SessionContext
 * 2. Immediately transitions to AnonConnectionPage (with placeholder UI)
 * 3. Starts WebSocket connection AND profile data request in parallel
 * 4. Photo loads in background - no blocking
 *
 * @param {Function} onReady - Callback when ready to show content (hides HelloScreen)
 */
const AnonExchangeFlow = ({ onReady }) => {
  // TIMING: Capture component mount time for performance measurement
  const T0 = useRef(Date.now());
  console.log(`[TIMING] T+0ms: AnonExchangeFlow mounted`);

  // Connection timing: start on mount (chatRoomId resolved below, updated in init useEffect)
  const timingStartedRef = useRef(false);

  // Prefetched data: populated by App.js from inline Supabase prefetch
  // serverRendered = true means profile data is available at mount (skip WebSocket wait)
  const serverData = window.__EXCHANGE_DATA__;
  const hasPrefetchedData = serverData?.serverRendered === true;

  // Use session context as the single source of truth for chatRoomId
  // App.js fast path already sets this before we mount
  const { sessionChatRoomId, updateSessionChatRoomId } = useSession();

  // Use connection context for proper state management
  const {
    updateProfileData,
    updateConnectionMetadata,
    updateNotes,
    updateChatMessages,
    updateContactSavedStatus,
    loadConnectionData,
  } = useConnectionContext();

  // Sans-chat hand-off (Step 3): share-back completion routes to People
  const { navigateTo } = useNavigation();

  // THE NEW-DESIGN ARM (signup E3a, F1): on branch deploys the guest's whole
  // view is the new design for every UNMINTED guest, first visit or return.
  // A view gate only — every formation effect below runs unchanged and never
  // reads it. Only under a ladder variant: a CHAT-tagged scan keeps the old
  // chat, so the mint's stored variant keeps isSimpleTier true. Read once —
  // neither the flag nor the tier can change mid-load.
  const [newGuest] = useState(() => isNewExperience() && hasDownstreamLadder() && !isSimpleTier());
  const [shareBackOpen, setShareBackOpen] = useState(false);

  // TEXT flow-open latch (plan 2, Gaps pass 1): once the TEXT journey starts
  // it OWNS the screen — participation flipping mid-flow (the share) must
  // never collapse the branch or fire the return door under the person.
  // Latched from the same virgin-room condition that would show a landing;
  // a participated returner never arms it (return door → People, as CARD).
  const [textFlowOpen, setTextFlowOpen] = useState(false);

  // chatRoomId: prefer prefetched data > session context > URL parsing
  const [localChatRoomId] = useState(() => {
    // Prefetched data has highest priority (already validated by App.js)
    if (serverData?.chatRoomId) return null; // Will use serverData.chatRoomId

    // Only parse URL if session doesn't have it yet (shouldn't happen in fast path)
    if (sessionChatRoomId) return null; // Will use sessionChatRoomId

    const urlParams = new URLSearchParams(window.location.search);
    let id = urlParams.get('chatRoomId') || urlParams.get('c');
    if (!id) {
      const search = window.location.search;
      if (search.startsWith('?') && search.length > 1) {
        const potentialId = search.substring(1).split('&')[0];
        if (potentialId.startsWith('chat_')) {
          id = potentialId;
        }
      }
    }
    return id;
  });

  // Single source of truth: prefetched data > session context > local URL
  const chatRoomId = serverData?.chatRoomId || sessionChatRoomId || localChatRoomId;

  // Host identity for the connections write AND the TEXT texted-link (both
  // read the same derivation — one home, hoisted from the connection effect)
  const hostProfileUserId =
    window.__EXCHANGE_DATA__?.profileUserId || window.location.pathname.split('/u/')[1];

  // Loading stage state: 'hello' -> 'ready' -> 'complete'
  // hello: Show "Hello" text (very brief - just until chatRoomId is parsed)
  // ready: Show AnonConnectionPage immediately (with placeholder if no data yet)
  // complete: Transition done, show Receive Contact Screen
  // Prefetched: Skip 'hello' entirely — data ready at mount, go straight to 'ready'
  const [loadingStage, setLoadingStage] = useState(hasPrefetchedData ? 'ready' : 'hello');

  // Connection error state - passed to AnonConnectionPage for error UX
  const [connectionError, setConnectionError] = useState(null);

  // ===== LANDING GATE (exchange-clarity D1) =====
  // The landing gates the VIEW only — every formation side-effect below
  // (markAnonConnected, confirms, WebSocket join) runs regardless; the dwell
  // buys the Profile the head start to speak first. Participation is derived
  // from room state (stored anon name / anon-sent history), never a
  // localStorage flag. undefined = the name read hasn't resolved yet — render
  // nothing landing-side for that beat so a returning Anon never sees a flash.
  const [storedAnonName, setStoredAnonName] = useState(undefined);
  // Dismissal is a per-room SESSION flag, not bare component state (bug found
  // at device pass: chat → detail → back remounts this screen and forgot the
  // [Open chat] tap, re-showing the landing mid-session). sessionStorage
  // survives in-session navigation and refresh, dies with the session — a
  // genuinely fresh visit still derives purely from room state (gate rule).
  const [landingDismissed, setLandingDismissed] = useState(
    () => sessionStorage.getItem(`landing_dismissed_${chatRoomId}`) === 'true'
  );

  // The entrance stagger rides the [Open chat] TAP only — deliberately PLAIN
  // state (resets on remount), never the session flag above: a detail
  // round-trip or reopen must not replay the arrival theater over settled
  // history (bug found at device pass — every remount staggered everything).
  // A COUNT, not a boolean (/simplify): the tap site owns the batch the
  // person actually saw waiting; messages beyond it land normally. The
  // landing only shows pre-participation, so the batch can only ever be the
  // Profile's opening messages.
  const [staggerCount, setStaggerCount] = useState(0);

  useEffect(() => {
    if (!chatRoomId) return;
    getAnonData(chatRoomId).then((data) => setStoredAnonName(data?.name || ''));
    // getAnonData is a stable module import; chatRoomId is the only real dep
  }, [chatRoomId]);

  // Get profile data from context to detect when it arrives
  // Also extract all data to pass as props to AnonConnectionPage (Fix 5 - prevent context re-renders)
  const contextData = useConnectionData(chatRoomId);
  const contextProfileData = contextData?.profileData || null;

  // Use prefetched data IMMEDIATELY on first render to avoid flash.
  // Without this: null → useEffect updates context → re-render with data = FLASH
  // With this: prefetched data on first render → WebSocket data when it arrives (seamless)
  const profileData = useMemo(() => {
    // Context data (from WebSocket) is most up to date
    if (contextProfileData) {
      return contextProfileData;
    }

    // Prefetched data available at mount — use immediately (before WebSocket arrives)
    if (hasPrefetchedData && serverData?.profileName) {
      return {
        name: serverData.profileName,
        photoUrl: serverData.profilePhoto || null,
        profileUserId: serverData.profileUserId,
        ...prefetchedLook(serverData),
      };
    }

    return null;
  }, [contextProfileData, hasPrefetchedData, serverData]);
  const connectionMetadata = contextData?.connectionMetadata || null;
  const notes = contextData?.notes || '';
  const hasSharedContactInfo = contextData?.hasSharedContactInfo || false;
  // Phase 4 Part 1: Initialize local chatMessages from context (handles page refresh)
  const [chatMessages, setChatMessages] = useState(() => {
    return contextData?.chatMessages || [];
  });

  // Sync local state from context (handles page refresh and DB background sync)
  useEffect(() => {
    if (contextData?.chatMessages?.length > 0) {
      setChatMessages(contextData.chatMessages);
    }
  }, [contextData?.chatMessages]);

  // Use ref to track latest profileData for retry logic
  const profileDataRef = useRef(null);
  useEffect(() => {
    profileDataRef.current = profileData;
  }, [profileData]);

  // Sans-chat full-card recovery (device pass 2, 2026-08-08: "the contact
  // icons didn't show this time"): the host's contacts ride ONE live
  // profile_data_response — the prefetch shell is name+photo only, and the
  // DB confirmation poll below is skipped whenever prefetch data exists, so
  // a missed WebSocket event left no re-delivery path. The payload IS
  // persisted in chat_messages; loadConnectionData already knows how to
  // recover it (the same backfill every mount runs, just re-run until the
  // real payload — always carrying sharedFields — lands). Bounded at 10
  // tries; stops the moment sharedFields arrive via any path.
  const hasFullCard = Boolean(profileData?.sharedFields);
  useEffect(() => {
    if (!isSansChatVariant() || !chatRoomId || hasFullCard) return undefined;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (tries > 10) {
        clearInterval(timer);
        return;
      }
      loadConnectionData(chatRoomId);
    }, 3000);
    return () => clearInterval(timer);
  }, [chatRoomId, hasFullCard, loadConnectionData]);

  // Foreground catch-up (device pass 1, 2026-08-13 — "shared data appears
  // instantly both ways"): the realtime channel dies while backgrounded, so
  // a venue the host confirmed meanwhile would wait for the next mount. One
  // loadConnectionData on every return implements the architecture's stated
  // catch-up layer ("picked up on mount or visibility change") on this screen.
  useEffect(() => {
    if (!chatRoomId) return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadConnectionData(chatRoomId);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [chatRoomId, loadConnectionData]);

  // Use ref to track chatMessages for duplicate detection without causing re-renders
  const chatMessagesRef = useRef([]);
  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);

  // Track if we've initialized to prevent re-running on chatRoomId changes
  const hasInitialized = useRef(false);

  // Confirmation poll interval — polls DB for host confirmation, cleared when profile_data_response arrives
  const confirmationPollRef = useRef(null);

  // Track if photo preload has started (prevents duplicate Image objects)
  const preloadStartedRef = useRef(false);

  // Track if analytics event has been logged (prevents duplicate logging)
  const hasLoggedAnalytics = useRef(false);

  // Signal ready (fade out Hello) as soon as we have the name
  // Photo loads in background - shows placeholder until ready
  // Moved before init useEffect so prefetch path can use it
  const [hasSignaledReady, setHasSignaledReady] = useState(hasPrefetchedData);

  // Initialize session state on mount (runs once)
  // chatRoomId is parsed in useState initializer for faster access
  useEffect(() => {
    // Only run initialization once
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // CRITICAL: Set user_type to 'anon' when AnonExchangeFlow loads
    // This ensures correct role detection for anonymous users
    sessionStorage.setItem('user_type', 'anon');
    console.log('AnonExchangeFlow: Set user_type to "anon" (entry point detection)');

    // Phase 0: Create + claim the persistent anon identity (f/identity-ownership).
    // This ID is used as senderId for all messages (single-identity model).
    // The mint is synchronous; the claim waits for sign-in. The prefetch path
    // arrives already claimed (App.js gates on it); the others hold the Hello
    // screen until it resolves — no write or live channel before the claim.
    const identityReady = ensureAnonIdentity();
    console.log('AnonExchangeFlow: Using anon_user_id:', getAnonUserId());

    // === PREFETCH FAST PATH ===
    // If prefetched data is available (from inline Supabase calls), skip WebSocket wait
    if (hasPrefetchedData && serverData?.profileName) {
      console.log(
        `[TIMING] T+${Date.now() - T0.current}ms: Prefetched data available, calling onReady`
      );

      // Connection timing: T0 = navigation start (QR tap)
      // startTiming captures browser nav milestones (ttfb, spinner_shown, prefetch)
      if (!timingStartedRef.current) {
        timingStartedRef.current = true;
        startTiming(chatRoomId, 'anon');
        mark('react_mount');
      }

      // Update session context with prefetched data
      if (!sessionChatRoomId) {
        updateSessionChatRoomId(chatRoomId);
      }

      // Persist prefetch data to localStorage so it survives navigation.
      // WebSocket profile_data_response will overwrite with full data when it arrives,
      // but if detection fails (missed connection), this is the only copy.
      updateProfileData(chatRoomId, {
        name: serverData.profileName,
        photoUrl: serverData.profilePhoto || null,
        profileUserId: serverData.profileUserId,
        ...prefetchedLook(serverData),
      });

      // === ANALYTICS: Log QR scan for prefetched pages ===
      const visitKey = `analytics_visits_${chatRoomId}`;
      const visitCount = parseInt(localStorage.getItem(visitKey) || '0', 10);
      if (visitCount === 0) {
        console.log('[ANALYTICS] Prefetch first visit - logging funnel_qr_scanned');
        logFunnelQRScanned(chatRoomId, serverData.profileName);
        localStorage.setItem(visitKey, '1');
        sessionStorage.setItem(`is_qr_session_${chatRoomId}`, 'true');
        hasLoggedAnalytics.current = true;
      }

      // Signal ready immediately - no need to wait for WebSocket
      setHasSignaledReady(true);
      onReady?.();
      return; // Skip normal initialization
    }

    // If we have chatRoomId (from session or URL), ensure session context is updated
    // and transition to ready state
    if (chatRoomId) {
      // Update session context if it came from local URL parsing
      if (!sessionChatRoomId) {
        updateSessionChatRoomId(chatRoomId);
        console.log('AnonExchangeFlow: Updated SessionContext with chatRoomId from URL');
      }
      console.log('AnonExchangeFlow: Using chatRoomId:', chatRoomId);

      // Ensure connection is discoverable in People list immediately
      // (mirrors what Profile side does in handleAnonDetected)
      // Guard: context is async so contextData?.connectionMetadata is null on first render.
      // Check localStorage directly (sync) to avoid overwriting existing metadata on return visits.
      // See data-and-state-guide.md "Direct localStorage Exceptions" §4.
      const existingMetadata = localStorage.getItem(`connection_metadata_${chatRoomId}`);
      if (!contextData?.connectionMetadata && !existingMetadata) {
        const seedMetadata = {
          lastUpdated: new Date().toISOString(),
        };
        updateConnectionMetadata(chatRoomId, seedMetadata);
        console.log('AnonExchangeFlow: Saved seed metadata for People list discoverability');
      }

      // Connection timing: T0 = navigation start (QR tap)
      if (!timingStartedRef.current) {
        timingStartedRef.current = true;
        startTiming(chatRoomId, 'anon');
        mark('react_mount');
      }

      // Set loading stage to 'ready' so AnonConnectionPage mounts (behind Hello)
      // once the id is claimed. DON'T call onReady yet - wait for profile data with name
      identityReady.then(() => {
        console.log('AnonExchangeFlow: Mounting AnonConnectionPage behind Hello screen');
        setLoadingStage('ready');
      });
    } else {
      console.error('AnonExchangeFlow: No chatRoomId found in URL or session');
    }
    // Note: hasPrefetchedData and serverData are constants from window.__EXCHANGE_DATA__
    // They don't change during component lifecycle, so not needed in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chatRoomId,
    sessionChatRoomId,
    updateSessionChatRoomId,
    onReady,
    updateProfileData,
    updateConnectionMetadata,
  ]);

  // WebSocket-based ready signal (for non-prefetch pages)
  // Prefetch pages already called onReady in the init useEffect above
  useEffect(() => {
    // Only signal once, and only when we have the name
    if (hasSignaledReady || preloadStartedRef.current || !profileData?.name) return;

    // Mark as started synchronously to prevent duplicates
    preloadStartedRef.current = true;

    const photoUrl = profileData.photoUrl;

    // Signal ready IMMEDIATELY when we have the name - don't wait for photo
    console.log(`[TIMING] T+${Math.round(performance.now())}ms: onReady called (Hello fades out)`);
    mark('ready');
    setHasSignaledReady(true);
    onReady?.();

    // Preload photo in background (so it's cached when component renders)
    if (photoUrl) {
      console.log('AnonExchangeFlow: Preloading photo in background:', photoUrl);
      const img = new Image();
      img.onload = () => {
        console.log('AnonExchangeFlow: Photo preloaded successfully');
      };
      img.onerror = () => {
        console.log('AnonExchangeFlow: Photo failed to preload');
      };
      img.src = photoUrl;
    }
  }, [profileData, hasSignaledReady, onReady]);

  // Handle messages from the chat room
  const handleMessage = useCallback(
    async (message) => {
      console.log('AnonExchangeFlow: Received message:', JSON.stringify(message));

      // Extract payload from Supabase broadcast message
      let processedMessage = message;
      if (
        message.event === 'message' &&
        message.type === 'broadcast' &&
        message.payload &&
        typeof message.payload === 'object'
      ) {
        processedMessage = message.payload;
        console.log(
          'AnonExchangeFlow: Extracted broadcast payload:',
          JSON.stringify(processedMessage)
        );
      } else if (message.payload && typeof message.payload === 'object') {
        processedMessage = message.payload;
        console.log('AnonExchangeFlow: Extracted payload:', JSON.stringify(processedMessage));
      }

      // Handle chat messages - SAVE to both local state AND context so they appear immediately
      if (processedMessage.type === 'chat') {
        console.log('AnonExchangeFlow: Received chat message:', processedMessage.content);

        // Create standardized message object
        const chatMessage = buildReceivedChatMessage(processedMessage);

        // Use functional update to avoid race conditions when multiple messages arrive quickly
        // This ensures we always read the latest state, not stale contextData
        setChatMessages((prevMessages) => {
          const isDuplicate = prevMessages.some(
            (msg) =>
              (msg.id && msg.id === chatMessage.id) ||
              (msg.timestamp === chatMessage.timestamp &&
                msg.content === chatMessage.content &&
                msg.sender === chatMessage.sender)
          );

          if (isDuplicate) {
            console.log('AnonExchangeFlow: Skipping duplicate chat message:', chatMessage.id);
            return prevMessages;
          }

          console.log('AnonExchangeFlow: Adding chat message to local state:', chatMessage);
          const newMessages = [...prevMessages, chatMessage];
          // Also persist to context for storage
          updateChatMessages(chatRoomId, newMessages);
          return newMessages;
        });
      }
      // System messages are created solely by ProfileConnectionPage and delivered
      // via DB (postgres_changes → globalHandler → addChatMessage → context).
      // No Anon-side creation needed.
      // Handle sent messages (contact messages)
      else if (processedMessage.type === 'sent') {
        console.log('AnonExchangeFlow: Received sent message:', processedMessage.content);
      }
      // Handle contact_share messages — either card-style (Profile) or inline contacts (Anon)
      // SAVE to both local state AND context so they appear immediately
      else if (processedMessage.type === 'contact_share') {
        console.log('AnonExchangeFlow: Received contact_share message:', processedMessage);

        let contactShareMessage;

        contactShareMessage = buildContactShareMessage(processedMessage);

        // Skip empty LEGACY inline shares — keyed on that shape's own
        // signature (a contacts[] array) so new variants pass by construction
        // (device pass 2: a flag-list guard ate the speech lines)
        if (
          Array.isArray(contactShareMessage.contacts) &&
          contactShareMessage.contacts.length === 0
        ) {
          console.log('AnonExchangeFlow: Skipping empty contact_share message');
          return;
        }

        // Use functional update to avoid race conditions when multiple messages arrive quickly
        setChatMessages((prevMessages) => {
          const isDuplicate = prevMessages.some(
            (msg) =>
              (msg.id && msg.id === contactShareMessage.id) ||
              (msg.type === 'contact_share' &&
                msg.timestamp === contactShareMessage.timestamp &&
                msg.sender === contactShareMessage.sender)
          );

          if (isDuplicate) {
            console.log(
              'AnonExchangeFlow: Skipping duplicate contact_share message:',
              contactShareMessage.id
            );
            return prevMessages;
          }

          console.log(
            'AnonExchangeFlow: Adding contact_share message to local state:',
            contactShareMessage
          );
          const newMessages = [...prevMessages, contactShareMessage];
          // Also persist to context for storage
          updateChatMessages(chatRoomId, newMessages);
          return newMessages;
        });
      }

      // Handle profile data response message — this IS the connection confirmation signal.
      // When Profile detects anon_connected in DB, it sends profile_data_response.
      // Receiving it proves: Profile is alive, Anon is subscribed, both share a chatRoomId.
      if (
        processedMessage.type === 'profile_data_response' &&
        processedMessage.sender === 'profile'
      ) {
        console.log(
          `[TIMING] T+${Date.now() - T0.current}ms: profile_data_response received — connection CONFIRMED`
        );

        mark('confirmed');

        // Stop the DB confirmation poll (optimization — WebSocket arrived first)
        if (confirmationPollRef.current) {
          clearInterval(confirmationPollRef.current);
          confirmationPollRef.current = null;
        }
        // Clear any error that may have fired before this arrived
        setConnectionError(null);

        // Extract profile data and connection metadata
        const { profileData, connectionMetadata } = processedMessage;

        // Handle profileData if present
        if (hasData(profileData)) {
          console.log('AnonExchangeFlow: Received profile data:', JSON.stringify(profileData));

          // Persistence handled by global handler in ConnectionStateContext
          // Component only owns: diagnostic logging + analytics

          // CRITICAL: Trust the sharedFields array exactly as Profile sent it
          const usedFallback = !profileData.sharedFields;

          // Log if we had to use fallback sharedFields
          if (usedFallback) {
            logConnectionError('shared_fields_fallback', {
              chatRoomId,
              received: JSON.stringify(Object.keys(profileData)),
              fallback: "['name', 'photoUrl']",
              profileUsername: profileData.name || null,
            });
          }

          // === ANALYTICS: Log qr_scanned for first visits ===
          // Note: link_clicked for return visits is logged on mount (doesn't need to wait for WebSocket)
          // Only log once per session (ref prevents duplicate logging on re-renders)
          console.log('[ANALYTICS_DEBUG] Checking analytics conditions:', {
            hasLoggedAnalytics: hasLoggedAnalytics.current,
            chatRoomId: chatRoomId,
            shouldLog: !hasLoggedAnalytics.current && chatRoomId,
          });
          if (!hasLoggedAnalytics.current && chatRoomId) {
            // Check if this is a first visit (QR scan)
            const visitKey = `analytics_visits_${chatRoomId}`;
            const visitCount = parseInt(localStorage.getItem(visitKey) || '0', 10);

            if (visitCount === 0) {
              // First visit - QR was just scanned, now we have profileUsername
              hasLoggedAnalytics.current = true;
              // Defensive: ensure profileUsername is a string (handle malformed data)
              const rawName = profileData.name;
              const profileUsername =
                typeof rawName === 'object'
                  ? rawName?.profileUsername || 'unknown'
                  : rawName || 'unknown';
              console.log('[ANALYTICS] First visit - logging funnel_qr_scanned');
              logFunnelQRScanned(chatRoomId, profileUsername);
              localStorage.setItem(visitKey, '1');
              // Mark this as the original QR session (cleared when browser closes)
              // Used to distinguish session signups from link signups
              sessionStorage.setItem(`is_qr_session_${chatRoomId}`, 'true');
            }
            // Note: Return visits (link_clicked) are already logged on mount
          }
        } else {
          console.log('AnonExchangeFlow: No profile data in this message');
        }

        // Metadata persistence handled by global handler in ConnectionStateContext
        if (hasData(connectionMetadata)) {
          console.log(
            'AnonExchangeFlow: Received connectionMetadata:',
            JSON.stringify(connectionMetadata)
          );
        }

        console.log('AnonExchangeFlow: Profile data response processing complete');
      }

      // Handle metadata_updated message (sent by Profile when venue is changed or location backfills)
      if (processedMessage.type === 'metadata_updated' && processedMessage.sender === 'profile') {
        console.log('[AnonExchangeFlow] Received metadata_updated from profile!');

        // Extract connection metadata with updated venue
        const { connectionMetadata } = processedMessage;

        if (hasData(connectionMetadata)) {
          console.log(
            '[AnonExchangeFlow] Processing metadata update:',
            JSON.stringify(connectionMetadata)
          );

          // Get existing metadata to preserve any fields not in the update
          const existingMetadata = contextData?.connectionMetadata || {};

          // Merge with existing metadata, preserving coordinates etc.
          const mergedMetadata = {
            ...existingMetadata,
            ...connectionMetadata,
            lastUpdated: new Date().toISOString(),
          };

          console.log('[AnonExchangeFlow] Final metadata to save:', JSON.stringify(mergedMetadata));

          // Update via context - handles BOTH localStorage AND React state atomically
          await updateConnectionMetadata(chatRoomId, mergedMetadata);
          console.log('[AnonExchangeFlow] Updated metadata via context (venue change synced)');

          // System message updates are handled by ProfileConnectionPage (sole creator).
          // When metadata changes, Profile's useEffect re-persists → postgres_changes UPDATE
          // → addChatMessage content-update → Anon sees updated content in real-time.
        } else {
          console.warn('[AnonExchangeFlow] metadata_updated message had no connectionMetadata');
        }
      }
    },
    [chatRoomId, updateConnectionMetadata, updateChatMessages, contextData?.connectionMetadata]
  );

  // Use ref for handleMessage so WebSocket effect doesn't re-run when handler changes
  const handleMessageRef = useRef(handleMessage);
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // Establish WebSocket connection and start confirmation timer.
  // The confirmation signal is profile_data_response from Profile's handleAnonDetected().
  // markAnonConnected() fires in parallel — triggers Profile's DB detection layers.
  useEffect(() => {
    let cleanupFunction = null;
    let isMounted = true;

    const initializeConnection = async () => {
      if (!chatRoomId) {
        console.error('AnonExchangeFlow: No chatRoomId, cannot initialize connection');
        return;
      }

      try {
        console.log(`[TIMING] T+${Date.now() - T0.current}ms: initializeConnection start`);
        mark('init_connection');

        // The guest id is claimed before any write or live channel (memoized —
        // instant on the prefetch path, which App.js already gated on it).
        await ensureAnonIdentity();
        if (!isMounted) return;

        // Signal Profile via database — fire in parallel with WebSocket join
        // Use raw fetch to avoid Supabase client stall on Android Chrome
        // A known guest re-scanning tells the host's LIVE QR which room they share
        signalHostOfGuest(chatRoomId).then((success) => {
          if (success) {
            console.log(`[TIMING] T+${Date.now() - T0.current}ms: anon_connected written to DB`);
            mark('db_write');
          } else {
            // All retries failed — Profile won't get real-time detection.
            // DB confirmation poll (checkHostConfirmed) handles user-facing error.
            // Recovery via connections table still works if confirmGuestConnection succeeded.
            console.warn('[ANON_EXCHANGE] host signal failed (no live session told):', chatRoomId);
          }
        });

        // Write to permanent connections table (Guest side, Anon↔Profile)
        // This is the FOUNDATION of missed-connection recovery — if this fails,
        // there's no connections row for Profile's People list recovery to find.
        // (chat_messages SELECT is USING (true) — migration 002, confirmed on
        // prod 2026-07-13 — so message delivery does NOT depend on
        // guest_auth_user_id; the row itself is what recovery needs.)
        const anonUserId = getAnonUserId();

        // First-time-init guard: a returning Anon routed back through the
        // exchange flow (UserRedirect recovery) must not re-date their origin —
        // the metadata stamp and the meeting seed below both write a fresh
        // meetingDate (the seed is create-if-missing PER DAY, so a return
        // visit would insert a phantom "WE MET today" card). meetingDate is
        // the virgin-exchange discriminator: the People-list discoverability
        // seed writes only lastUpdated. Read via the storage service, not
        // context — context is async on first render.
        const existingOriginMetadata = await getConnectionMetadata(chatRoomId);
        const isVirginExchange = !existingOriginMetadata?.meetingDate;
        if (!isVirginExchange) {
          console.log('[ANON_EXCHANGE] Return visit — keeping original origin metadata');
        }
        const originMetadata = isVirginExchange
          ? {
              meetingDate: new Date().toISOString(),
              meetingUtcOffset: currentUtcOffsetMinutes(),
              via: 'scan',
              exchangeType: 'anon_profile',
            }
          : null;

        try {
          await confirmGuestConnection(chatRoomId, hostProfileUserId, anonUserId, 'anon_profile');
          if (isVirginExchange) {
            // Create connection_views row for anon user (fire-and-forget after connections row
            // exists). The meeting row is filed when the host's record arrives, under the
            // host's meeting key — the host decides the meeting (ConnectionStateContext).
            upsertConnectionView(chatRoomId, anonUserId);
          }
        } catch (err) {
          console.error('[ANON_EXCHANGE] confirmGuestConnection failed:', err);
          logConnectionError('guest_confirmation_failed', {
            chatRoomId,
            message: err.message || 'confirmGuestConnection threw unexpectedly',
          });
        }

        // Persist connection metadata so Anon's People list shows this connection
        // even if Profile's detection never fires (missed connection scenario).
        // WebSocket profile_data_response will overwrite with richer metadata when it arrives.
        if (isVirginExchange) {
          updateConnectionMetadata(chatRoomId, originMetadata);
        }

        // Start DB confirmation poll BEFORE joinChatRoom — these are independent.
        // The poll checks the connections table (DB). joinChatRoom opens a WebSocket.
        // If WebSocket fails/times out, the poll still runs and catches confirmation.
        if (isMounted && !profileDataRef.current) {
          let successfulFailures = 0; // Only count definitive "not confirmed" responses
          confirmationPollRef.current = setInterval(async () => {
            // Stop polling if profile_data_response already arrived (fast-path optimization)
            if (profileDataRef.current) {
              clearInterval(confirmationPollRef.current);
              confirmationPollRef.current = null;
              return;
            }

            const confirmed = await checkHostConfirmed(chatRoomId);

            if (confirmed === true) {
              console.log('[ANON_EXCHANGE] Host confirmed via DB poll');
              clearInterval(confirmationPollRef.current);
              confirmationPollRef.current = null;
              setConnectionError(null);
              return;
            }

            if (confirmed === null) {
              // Query failed (network issue) — inconclusive, keep trying
              console.log('[ANON_EXCHANGE] DB poll: query failed, will retry');
              return;
            }

            // confirmed === false — query succeeded, no confirmation yet
            successfulFailures++;

            // After 5 definitive "not confirmed" responses → show error
            if (successfulFailures >= 5) {
              console.error('AnonExchangeFlow: No host confirmation after 5 successful DB polls');
              logConnectionError('connection_not_confirmed', {
                chatRoomId,
                successfulFailures,
                hadPrefetchData: !!window.__EXCHANGE_DATA__?.serverRendered,
                profileUsername: window.__EXCHANGE_DATA__?.profileName || null,
              });
              clearInterval(confirmationPollRef.current);
              confirmationPollRef.current = null;
              setConnectionError({
                type: 'timeout',
                message: 'Connection not confirmed',
                canRetry: false,
              });
            }
          }, 3000);
        }

        mark('ws_join_start');
        const joinStart = Date.now();
        const cleanup = await joinChatRoom(chatRoomId, (msg) => handleMessageRef.current(msg));
        console.log(
          `[TIMING] T+${Date.now() - T0.current}ms: joinChatRoom resolved (took ${Date.now() - joinStart}ms)`
        );
        mark('ws_join_complete');
        flush(); // Anon "chat ready" — total_ms = QR tap → WebSocket joined
        cleanupFunction = cleanup;
      } catch (error) {
        // joinChatRoom failed — log it but don't set connectionError.
        // The DB confirmation poll is already running independently and will
        // determine whether the connection actually succeeded.
        console.error('AnonExchangeFlow: Error in initializeConnection:', error);
        logConnectionError('websocket_anon', {
          message: error.message || 'WebSocket connection failed',
          chatRoomId,
          profileUsername: profileDataRef.current?.name || null,
        });
      }
    };

    if (chatRoomId) {
      initializeConnection();
    }

    return () => {
      console.log('AnonExchangeFlow: Cleaning up connection');
      isMounted = false;
      if (confirmationPollRef.current) {
        clearInterval(confirmationPollRef.current);
        confirmationPollRef.current = null;
      }
      if (cleanupFunction) {
        cleanupFunction();
      }
    };
  }, [chatRoomId, updateConnectionMetadata, hostProfileUserId]); // handleMessage accessed via ref - no need to re-run on handler changes

  // Handle transition animation complete
  const handleTransitionComplete = () => {
    if (loadingStage === 'ready') {
      console.log('AnonExchangeFlow: Fade-in animation complete');
      setLoadingStage('complete');
    }
  };

  // Landing gate derivation (D1). connectionError drops the gate so the
  // existing "scan again" banner UX (inside AnonConnectionPage) is unchanged.
  const showLanding =
    storedAnonName !== undefined &&
    !landingDismissed &&
    !connectionError &&
    !hasAnonParticipation({ anonName: storedAnonName, chatMessages });

  // THREAD interstitial gate (plan 3 Step 4). Per-room sessionStorage, the
  // same convention TEXT's interstitial uses: a detail round-trip inside the
  // session must not replay the card, but a genuinely fresh visit derives from
  // room state like every other gate. Pure view gate — formation already ran.
  const [threadInterstitialDismissed, setThreadInterstitialDismissed] = useState(
    () => sessionStorage.getItem(`thread_interstitial_dismissed_${chatRoomId}`) === 'true'
  );
  const showThreadInterstitial = showLanding && !threadInterstitialDismissed;

  const handleThreadInterstitialDismiss = () => {
    sessionStorage.setItem(`thread_interstitial_dismissed_${chatRoomId}`, 'true');
    setThreadInterstitialDismissed(true);
  };

  // TEXT flow-open latch: arms from the same virgin-room condition that
  // would show a landing, then stays open for the whole journey (interstitial
  // → card → sheet → post-share) — mid-flow participation can't close it.
  useEffect(() => {
    if (isTextVariant() && showLanding && !textFlowOpen) setTextFlowOpen(true);
  }, [showLanding, textFlowOpen]);

  // Sans-chat return door (D6): a participated returner never sees chat —
  // People is home. Fires only once the name read has resolved (undefined =
  // still loading) and never during an error (the error surface stays put) or
  // an in-progress share (storedAnonName is a mount read — completion
  // navigates explicitly before this could re-fire).
  const redirectedToPeopleRef = useRef(false);
  useEffect(() => {
    // The new design owns its returners (it resumes them in place)
    if (newGuest) return;
    if (!isSansChatVariant() || redirectedToPeopleRef.current) return;
    if (shareBackOpen || textFlowOpen) return; // mid-flow: the flow owns navigation
    if (storedAnonName === undefined || showLanding || connectionError) return;
    redirectedToPeopleRef.current = true;
    updateSessionChatRoomId(chatRoomId);
    navigateTo('anon-connections-list', { source: 'return-visit' });
  }, [
    newGuest,
    showLanding,
    shareBackOpen,
    textFlowOpen,
    storedAnonName,
    connectionError,
    chatRoomId,
    navigateTo,
    updateSessionChatRoomId,
  ]);

  // funnel_landing_shown / funnel_landing_open_chat retired (Step 8) — the
  // chat-landing they measured is gone; the 4-step funnel replaces them.
  const handleLandingOpenChat = () => {
    sessionStorage.setItem(`landing_dismissed_${chatRoomId}`, 'true');
    setStaggerCount(chatMessages.length);
    setLandingDismissed(true);
  };

  // Sans-chat handlers (Step 3) — reachable only under the CARD/TEXT variants.
  const handleShareBackStart = () => setShareBackOpen(true);

  // "Just save" — get without give (A6: the connection is already formed by
  // the mount effects). Raises the iOS contacts sheet via the shared vCard
  // path and stays on the card — never an app entry (flow simplification #3).
  const handleJustSave = async () => {
    if (!profileData) return;
    const freshMetadata = await getConnectionMetadata(chatRoomId);
    await saveContactToPhone({
      userData: profileData,
      chatRoomId,
      connectionMetadata: freshMetadata,
      nickname: '',
      viewerIsProfile: false,
      updateContactSavedStatus,
    });
    // Funnel step 1 (deduped per room): just-save completes the exchange too
    logFunnelExchangeCompleted(chatRoomId, 'just_save');
  };

  // Name screen Next (user ruling, device pass 1): announce the name to the
  // host IMMEDIATELY — the host's waiting card becomes a named card while the
  // person is still typing their contact, and the host gets the venue
  // confirm/edit window early. Worst case: a named card with no contact.
  const handleShareBackName = (name) => {
    announceAnonName(chatRoomId, name);
  };

  // Share-back completion — the ONE authoritative contact write, then the
  // inline→routed hand-off to People. Session id set BEFORE navigate (A3
  // caveat a: the routed screens select by sessionChatRoomId; params alone
  // are silently ignored).
  const handleShareBackComplete = async ({ name, contactType, contactValue }) => {
    await commitAnonContactRecord(chatRoomId, contactType, contactValue, { fallbackName: name });
    // Funnel step 1 (deduped per room): the exchange completed with a share-back
    logFunnelExchangeCompleted(chatRoomId, 'share_back');
    updateSessionChatRoomId(chatRoomId);
    navigateTo('anon-connections-list', { source: 'share-back' });
  };

  // New design (E3a): the contact sheet's commit — the ONE authoritative
  // contact write for this user action, then funnel step 1 (deduped per room).
  // The name was already announced at the name sheet (a separate action).
  const handleGuestContact = async (contactType, contactValue) => {
    const anonData = await commitAnonContactRecord(chatRoomId, contactType, contactValue);
    logFunnelExchangeCompleted(chatRoomId, 'share_back');
    return anonData;
  };

  // New design: Done minted the Simple profile → new People (F4). Session id
  // first, as every routed hand-off here does.
  const handleGuestMinted = () => {
    updateSessionChatRoomId(chatRoomId);
    navigateTo('new-people', { arrivedRoom: chatRoomId }, true);
  };

  // TEXT share completion (plan 2; channel selection at device pass 1j):
  // ONE authoritative write — the sheet's single submit carries the chosen
  // channel + name together, so there is no announce step (same-id upsert
  // race rule). NO navigation: the flow stays on the card showing the
  // post-share states.
  const handleTextShareComplete = async ({ contactType, contactValue, name }) => {
    await commitAnonContactRecord(chatRoomId, contactType, contactValue, { fallbackName: name });
    logFunnelExchangeCompleted(chatRoomId, 'share_back');
  };

  // TEXT back door (post-share, the pulled ladder): out to People.
  const handleTextBack = () => {
    updateSessionChatRoomId(chatRoomId);
    navigateTo('anon-connections-list', { source: 'text-flow-back' });
  };

  // One read for the render seam — the variant can't change mid-load
  const sansChat = isSansChatVariant();

  // Don't render anything during 'hello' stage - App.js shows HelloScreen
  // This prevents the flash issue when navigating within the app
  if (loadingStage === 'hello') {
    return null;
  }

  // The new-design guest's frame matches its screen (device pass 2026-10-20, 3):
  // GuestExchangeFlow is 100lvh plus the Safari bleed, held scrolled by that bleed
  // (useShellChrome), so a 100vh clip here cut off its bottom 64 px — Connect and
  // the sheets. Every other arm keeps the old frame exactly.
  const frameStyle = newGuest
    ? {
        height: `calc(100lvh + ${safariBleed()}px)`,
        width: '100vw',
        position: 'relative',
        overflow: 'visible',
      }
    : { height: '100vh', width: '100vw', position: 'relative', overflow: 'hidden' };

  return (
    <div style={frameStyle}>
      {/* AnonConnectionPage - shown after profile data + photo loaded */}
      {/* For prefetched pages, skip fade animation - content should appear instantly */}
      {(loadingStage === 'ready' || loadingStage === 'complete') && chatRoomId && (
        <motion.div
          key="connection"
          initial={{ opacity: hasPrefetchedData ? 1 : 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: hasPrefetchedData ? 0 : 0.4 }}
          onAnimationComplete={handleTransitionComplete}
          style={{
            position: 'absolute',
            inset: 0,
          }}
        >
          {/* Landing (D1) — view gate on a virgin room; [Open chat] reveals
              the chat. Join door removed until the invite epic's claim flow
              (2026-07-27 user call). */}
          {/* THE VARIANT SEAM (exchangeVariant.js), a 4-way since plan 3:
              CHAT renders the landing→chat flow; CARD the lo-fi card +
              share-back wizard; TEXT its own flow (interstitial → card →
              sheet → post-share, epic §8); THREAD renders NO landing at all —
              the thread itself is the landing, so it falls straight through to
              AnonConnectionPage, where the composer is gated instead (plan 3
              Step 3). Formation effects above are untouched under every
              variant. */}
          {/* The active flow OWNS the screen while open — above the landing
              gate, so participation flipping mid-flow (CARD's name announce,
              TEXT's share) can never collapse the branch under the user.
              TEXT renders on (textFlowOpen || showLanding): the latch effect
              runs a tick after the first virgin render. */}
          {newGuest ? (
            <Suspense fallback={null}>
              <GuestExchangeFlow
                chatRoomId={chatRoomId}
                profileData={profileData}
                connectionMetadata={connectionMetadata}
                onSaveToPhone={handleJustSave}
                onName={handleShareBackName}
                onContact={handleGuestContact}
                onMinted={handleGuestMinted}
                connectionError={connectionError}
              />
            </Suspense>
          ) : sansChat && shareBackOpen ? (
            <AnonShareBackFlow
              onNameNext={handleShareBackName}
              onComplete={handleShareBackComplete}
            />
          ) : isTextVariant() && (textFlowOpen || showLanding) ? (
            <AnonTextFlow
              chatRoomId={chatRoomId}
              profileData={profileData}
              connectionMetadata={connectionMetadata}
              chatMessages={chatMessages}
              hasSharedContactInfo={hasSharedContactInfo}
              hostProfileUserId={hostProfileUserId}
              onShareComplete={handleTextShareComplete}
              onSaveToPhone={handleJustSave}
              onBack={handleTextBack}
            />
          ) : showLanding && !isThreadVariant() ? (
            sansChat ? (
              <AnonCardLanding
                profileData={profileData}
                onShareBack={handleShareBackStart}
                onJustSave={handleJustSave}
              />
            ) : (
              <AnonLandingPage profileData={profileData} onOpenChat={handleLandingOpenChat} />
            )
          ) : (
            <AnonConnectionPage
              source="qr-scan"
              // Fix: Pass chatRoomId as prop to avoid race condition with sessionChatRoomId
              chatRoomId={chatRoomId}
              // Landing → chat reveal (2026-07-27 user call): the messages
              // already waiting land one after another, not as a dump. Set
              // only by THIS tap (see staggerCount).
              entranceStaggerCount={staggerCount}
              // Fix 5: Pass data as props to prevent context subscription re-renders
              profileData={profileData}
              connectionMetadata={connectionMetadata}
              notes={notes}
              chatMessages={chatMessages}
              hasSharedContactInfo={hasSharedContactInfo}
              // Pass context update methods
              updateProfileData={updateProfileData}
              updateConnectionMetadata={updateConnectionMetadata}
              updateNotes={updateNotes}
              updateChatMessages={updateChatMessages}
              // Pass connection error for error UX
              connectionError={connectionError}
            />
          )}

          {/* THREAD's interstitial (plan 3 Step 4, anon_1) sits OVER the
              thread rather than replacing it — the connection is visibly real
              behind the card, which is the variant's premise. Same virgin-room
              condition that would have shown a landing, so a participated
              returner never sees it. */}
          {!newGuest && isThreadVariant() && showThreadInterstitial && (
            <AnonThreadInterstitial
              chatRoomId={chatRoomId}
              profileData={profileData}
              onDismiss={handleThreadInterstitialDismiss}
            />
          )}
        </motion.div>
      )}

      {/* Fallback for no chatRoomId */}
      {!chatRoomId && <div></div>}
    </div>
  );
};

// Memoize to prevent re-renders when App's Hello state changes
// AnonExchangeFlow's props (onReady) are stable, so this prevents unnecessary re-renders
export default memo(AnonExchangeFlow);
